/* Keep native search routing and pagination; reject unsupported semantic fallbacks. */
(() => {
 if (window.sokyndSearch) return;
 const cache = new Map();
 let navigationRevision = 0;
 const fold = text => String(text || '').replace(/<[^>]*>/g, ' ').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
 const words = text => fold(text).match(/[\p{L}\p{N}]+/gu) || [];
 const quote = text => words(text).map(word => JSON.stringify(word)).join(' AND ');
 const advanced = text => /[":*()]|\b(?:AND|OR|NOT)\b|(?:^|\s)-\S/.test(text);
 function near(a, b) {
  if (a === b) return true;
  if (a.length < 5 || Math.abs(a.length - b.length) > 1) return false;
  let i = 0, j = 0, edits = 0;
  while (i < a.length && j < b.length) {
   if (a[i] === b[j]) { i++; j++; continue; }
   if (++edits > 1) return false;
   if (a.length === b.length && a[i] === b[j + 1] && a[i + 1] === b[j]) { i += 2; j += 2; }
   else if (a.length > b.length) i++;
   else if (b.length > a.length) j++;
   else { i++; j++; }
  }
  return edits + Number(i < a.length || j < b.length) <= 1;
 }
 function supports(product, tokens) {
  const tags = (product.tags || []).filter(tag => !String(tag).startsWith('_'));
  const vocabulary = new Set(words([product.title, product.body, product.type,
   product.handle, product.vendor, product.featured_image?.alt, ...tags].join(' ')));
  return tokens.every(token => [...vocabulary].some(word => word === token ||
   (token.length >= 3 && word.startsWith(token)) ||
   (token.length >= 4 && word.includes(token)) || near(token, word)));
 }
 async function resolve(terms) {
  const raw = terms.trim();
  const unchanged = {query: raw, displayTerms: raw, guarded: false};
  const tokens = words(raw).filter(word => word.length > 1);
  if (!tokens.length || advanced(raw)) return unchanged;
  const url = new URL(theme.routes.predictiveSearch, location.origin);
  if (!url.pathname.endsWith('.json')) url.pathname += '.json';
  const key = url.origin + url.pathname + ':' + document.documentElement.lang + ':' + raw;
  if (cache.has(key)) return cache.get(key);
  const pending = (async () => {
   const controller = new AbortController();
   const timeout = setTimeout(() => controller.abort(), 3000);
   try {
    url.searchParams.set('q', raw);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', '10');
    url.searchParams.set('resources[options][fields]', 'title,body,product_type,variants.title,vendor,tag,variants.sku');
    const response = await fetch(url, {signal: controller.signal});
    if (!response.ok) { cache.delete(key); return unchanged; }
    const data = await response.json();
    const products = data?.resources?.results?.products;
    if (!Array.isArray(products)) { cache.delete(key); return unchanged; }
    const hasVariant = product => Array.isArray(product.variants) && product.variants.length;
    if (tokens.some(token => /\d/.test(token)) && products.some(hasVariant)) return unchanged;
    const strict = {query: quote(raw), displayTerms: raw, guarded: true};
    if (document.documentElement.lang.toLowerCase().split('-')[0] === 'en') {
     const exactUrl = new URL(url);
     exactUrl.searchParams.set('q', strict.query);
     const exactResponse = await fetch(exactUrl, {signal: controller.signal});
     if (!exactResponse.ok) { cache.delete(key); return unchanged; }
     const exactData = await exactResponse.json();
     const exactProducts = exactData?.resources?.results?.products;
     if (!Array.isArray(exactProducts)) { cache.delete(key); return unchanged; }
     if (exactProducts.length) return strict;
    }
    const supported = products.filter(product => supports(product, tokens) || hasVariant(product));
    if (supported.length) return {...unchanged, handles: supported.map(product => product.handle)};
    // Quotes disable Shopify's unrelated semantic suggestions on the server.
    return strict;
   } catch { cache.delete(key); return unchanged; }
   finally { clearTimeout(timeout); }
  })();
  if (cache.size >= 50) cache.delete(cache.keys().next().value);
  cache.set(key, pending);
  return pending;
 }
 function apply(url, result) {
  url.searchParams.set('q', result.query);
  if (result.guarded) url.searchParams.set('sokynd_q', result.displayTerms);
  else url.searchParams.delete('sokynd_q');
  return url;
 }
 function displayTerms(url) {
  const source = url.searchParams.get('sokynd_q');
  return source && !advanced(source) && quote(source) === url.searchParams.get('q') ? source : url.searchParams.get('q') || '';
 }
 window.sokyndSearch = {resolve, apply, displayTerms};

 document.addEventListener('submit', async event => {
  const form = event.target;
  if (!form.matches('.main-search__form')) return;
  event.preventDefault();
  const revision = ++navigationRevision;
  const input = form.querySelector('input[name="q"]');
  const terms = input.value.trim();
  const result = await resolve(terms);
  if (input.value.trim() !== terms || revision !== navigationRevision) return;
  const url = new URL(form.action, location.origin);
  new FormData(form).forEach((value, key) => url.searchParams.set(key, value));
  location.assign(apply(url, result).href);
 });
 document.addEventListener('click', async event => {
  const link = event.target.closest('.store-search__footer a');
  if (!link || event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const input = link.closest('main-search')?.querySelector('input[name="q"]');
  if (!input) return;
  event.preventDefault();
  const revision = ++navigationRevision;
  const terms = input.value.trim();
  const result = await resolve(terms);
  if (input.value.trim() === terms && revision === navigationRevision) location.assign(apply(new URL(link.href), result).href);
 });
 async function normalizePage() {
  const page = document.querySelector('.sokynd-search[data-section-id]');
  if (!page) return;
  const url = new URL(location.href);
  const q = url.searchParams.get('q') || '';
  const display = displayTerms(url);
  const input = page.querySelector('input[name="q"]');
  const revision = navigationRevision;
  if (display !== q) {
   const restore = () => {
    const current = new URL(location.href);
    const query = current.searchParams.get('q') || '';
    const label = displayTerms(current);
    if (label === query) return;
    const field = page.querySelector('.main-search__form input[name="q"]');
    if (field && field.value === query) field.value = label;
    const empty = page.querySelector('.sokynd-search-empty');
    if (empty && empty.textContent.includes(query)) empty.textContent = empty.textContent.replace(query, label);
    document.title = document.title.replace(query, label);
    page.querySelectorAll('a[href]').forEach(link => {
     const target = new URL(link.href, location.origin);
     if (target.origin === current.origin && target.pathname === current.pathname &&
         (target.searchParams.get('q') === query || (!target.searchParams.has('q') && target.searchParams.has('sort_by')))) {
      target.searchParams.set('q', query);
      target.searchParams.set('sokynd_q', label);
      link.href = target.href;
     }
    });
    page.querySelectorAll('filter-container form').forEach(form => {
     let marker = form.querySelector('input[name="sokynd_q"]');
     if (!marker) { marker = document.createElement('input'); marker.type = 'hidden'; marker.name = 'sokynd_q'; form.appendChild(marker); }
     marker.value = label;
    });
   };
   restore();
   new MutationObserver(restore).observe(page, {childList: true, subtree: true});
   return;
  }
  if (!q || advanced(q)) return;
  page.setAttribute('data-search-normalizing', '');
  try {
   const result = await resolve(q);
   if (location.href !== url.href || revision !== navigationRevision || (input && input.value.trim() !== q)) return;
   if (result.query !== q) { location.replace(apply(url, result).href); return; }
  } finally { page.removeAttribute('data-search-normalizing'); }
 }
 document.addEventListener('DOMContentLoaded', normalizePage);
})();
